/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo } from 'react';
import { jsx, css } from '@emotion/react';
import { colors } from '@atlaskit/theme';
import { useQuery } from 'react-query';
import { OnSuggestionSelected } from 'react-autosuggest';
import Textfield from 'components/textfield';
import Autosuggest from './Autosuggest';
import api from '../../libs/api';
import { Company } from '../select/types';
import { useDebounce } from '../../hooks/useDebounce';
import { ItemContainer } from './styled';

type Props = Omit<React.ComponentProps<typeof Textfield>, 'theme' | 'onSelect' | 'onChange' | 'value'>  & {
  value: string;
  onChange: (companyName: string) => void;
  onSelect: (company: Company) => void;
  autoFocus: boolean;
};

type CompanySuggestion = Company;
type OrganisationsData = {
  organisations: {
    KvK?: Company[];
  };
};
const fetchCompanySuggestions = async (key: string, query: string) => {
  const data: OrganisationsData = await api.get('/Organisations/search?', {
    params: { query },
  });
  if (data.organisations?.KvK) {
    return data.organisations.KvK;
  }
  return [];
};

const getDetailedCompanySuggestion = async ({
  companyRegistrationNumber,
  name,
}: Company) => {
  const { organisation } = await api.get('/Organisations/kvk', {
    params: {
      companyRegistrationNumber,
      companyName: name,
    },
  });
  return organisation;
};

const renderSuggestion = ({ name, address }: Company) => (
  <ItemContainer
    css={css`
    `}
  >
    <div
      css={css`
        font-weight: 500;
      `}
    >
      {name}
    </div>
    <div
      css={css`
        color: ${colors.subtleText()};
        font-size: 0.8rem;
      `}
    >
      {address && [address.line1, address.postalCode, address.city].join(', ')}
    </div>
  </ItemContainer>
);

const getSuggestionValue = (company: Company) => company.name;

const CompanySuggest: React.FC<Props> = (props) => {
  const { value, onChange, onSelect, autoFocus, ...rest } = props;
  const query = useDebounce(value, 400);

  const { data, error, isLoading } = useQuery(
    ['companySuggestions', query],
    fetchCompanySuggestions
  );
  const inputProps = useMemo(() => {
    return { value, onChange };
  }, [value, onChange]);
  const handleOnSuggestionSelected = useCallback<OnSuggestionSelected<Company>>(
    (event, data) => {
      const company = data.suggestion;
      onSelect(company);
      (async () => {
        const detailedCompanySuggestion = await getDetailedCompanySuggestion(
          company
        );
        onSelect(detailedCompanySuggestion);
      })();
    },
    []
  );

  return (
    <Autosuggest
      {...rest}
      suggestions={data || []}
      value={value}
      onChange={onChange}
      onSuggestionSelected={handleOnSuggestionSelected}
      renderSuggestion={renderSuggestion}
      getSuggestionValue={getSuggestionValue}
      autoFocus={autoFocus}
    />
  );
};

export default CompanySuggest;
